import React, { useEffect, Suspense, useContext } from "react";
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import LoginPage from "./Components/LoginPage";
import AdminShell from "./Layouts/AdminShell";
import { ErrorBoundary } from "./Components/common/ErrorBoundary";
import Skeleton from "./Components/common/Skeleton";
import { routes } from "./Routes/config";
import { GlobalContext } from "./Context/GlobalContext";
// ✅ NEW: import the Clients page
import AdminClients from "./Components/Clients/AdminClients";
import TabbedPayoutLogs from "./Components/Payments/AdminPayoutSection";
import AdminUpiPayment from "./Components/Payments/AdminUpiPayment";
import AdminCampaignLog from "./Components/WhatsApp/AdminCampaignLog";
import AdminWhatsApp from "./Components/WhatsApp/AdminWhatsApp";
import AdminTemplate from "./Components/WhatsApp/AdminTemplate";
import AdminTemplateView from "./Components/WhatsApp/AdminTemplateView";
import AdminCampaign from "./Components/WhatsApp/AdminCampaign";
import AdminCampaignView from "./Components/WhatsApp/AdminCampaignView";
import AdminWhiteLabels from "./Components/Clients/AdminWhiteLabels";
import AdminSupportTickets from "./Components/Support/Tickets/AdminSupportTickets";
import Support from "./Components/Support/SupportChat/Support";
import AdminOrderController from "./Components/Orders/AdminOrderController";
import AdminPosOrders from "./Components/Orders/AdminPosOrders";
import AdminDining from "./Components/Orders/Dining/AdminDining";
import AdminQrCode from "./Components/Orders/Dining/AdminQrCode";
import AdminOrderSettlement from "./Components/Orders/Settlement/AdminOrderSettlement";
import AdminPaymentReports from "./Components/Orders/Settlement/AdminPaymentReports";
import AdminSettlementAnalytics from "./Components/Orders/Settlement/AdminSettlementAnalytics";
import AdminSettlement from "./Components/Orders/Settlement/AdminSettlement";
import AdminDashboard from "./Components/Dashboard/AdminDashboard";
import ClientInsights from "./Components/Billing/ClientInsights";
import ClientBills from "./Components/Billing/ClientBills";
import AdminBilling from "./Components/Billing/AdminBilling";
import OrderHub from "./Components/OrderSuite/OrderHub";
import Orders from "./Components/Orders Main/Orders";
import PayOut from "./Components/Orders/Settlement/PayOut";
import SupperAdmin from "./Components/Super Admin/SupperAdmin";
import AdminAllTransactionsReport from "./Components/Payments/AdminAllTransactionsReport";
import ClientConfiguration from "./Components/Clients/ClientConfiguration";
import RestaurantWiseTransactions from "./Components/Orders/Settlement/RestaurantWiseTransactions";
import Recon from "./Components/Recon/Recon";
import ReconAllOrders from "./Components/Recon/ReconAllOrders";
import ReconOrderController from "./Components/Recon/ReconOrderController";
import ReconAllTransactionsReport from "./Components/Recon/ReconTransactions";
import ReconSettlement from "./Components/Recon/ReconSettlement";
import SettlementReport from "./Components/Recon/SubComponents/ReconSettlementReport";
import ReconRewise from "./Components/Recon/ReconRewise";
import ComingSoon from "./Components/common/ComingSoon";
import NotAllowed from "./Components/common/NotAllowed";
import GlobalInventory from "./Components/Inventory/GlobalInventory";
import AdminChaafo from "./Components/Chaafo/AdminChaafo";
import AdminSubdomainTabs from "./Components/Domain/AdminSubdomainTabs";
import AdminCatalogue from "./Components/Orders/Catalogue/AdminCatalogue";
import EditIngredients from "./Components/Orders/Catalogue/EditIngredients";

/* ---------------- Helpers & Guards ---------------- */
function getSessionUser() {
  try {
    const raw = sessionStorage.getItem("userData");
    if (!raw) return null;
    const user = JSON.parse(raw);
    if (user?.tokenExpiry && Date.now() > Number(user.tokenExpiry)) {
      sessionStorage.removeItem("userData");
      return null;
    }
    return user;
  } catch {
    return null;
  }
}

function ProtectedRoute({ children }) {
  const location = useLocation();
  const user = getSessionUser();
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return children;
}

function GuestRoute({ children }) {
  const user = getSessionUser();
  if (user) {
    return <Navigate to="/" replace />;
  }
  return children;
}
/*------------------USER ACEESS-------------------*/
function normalizePath(path = "") {
  return path.toLowerCase().replace(/\/+$/, "");
}

function AccessRoute({ children }) {
  const location = useLocation();

  const { userAccess } = useContext(GlobalContext);

  const currentPath = normalizePath(location.pathname);

  const moduleAccessMap = {
    "/admin/templates": "/admin/whatsapp",
    "/admin/templatesview": "/admin/whatsapp",
    "/admin/campaigns": "/admin/whatsapp",
    "/admin/campaignview": "/admin/whatsapp",
  };

  const hasAccess =
    Array.isArray(userAccess) &&
    userAccess.some((route) => {
      const normalizedRoute = normalizePath(route);

      // exact
      if (currentPath === normalizedRoute) {
        return true;
      }

      // nested
      if (currentPath.startsWith(normalizedRoute + "/")) {
        return true;
      }

      // mapped module access
      return Object.entries(moduleAccessMap).some(([child, parent]) => {
        return currentPath.startsWith(child) && normalizedRoute === parent;
      });
    });

  if (!hasAccess) {
    return <NotAllowed />;
  }

  return children;
}
/* -------------------------------------------------- */

/* ---------------------------- App Wrapper (routes) ---------------------------- */
function AppWrapper() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentAdmin } = useContext(GlobalContext);

  const userID = currentAdmin?.userid;

  useEffect(() => {
    // ⚠️ Do NOT nuke localStorage userData here; it can cause redirect flicker.
    const raw = sessionStorage.getItem("userData");
    if (!raw && location.pathname !== "/login") {
      navigate("/login", { replace: true });
    }
  }, [navigate, location.pathname]);

  const role = (currentAdmin?.roleName || "").toLowerCase();

  return (
    <>
      <Routes>
        {/* Public (guest-only) */}
        <Route
          path="/login"
          element={
            <GuestRoute>
              <LoginPage onLogin={() => navigate("/")} />
            </GuestRoute>
          }
        />

        {/* Protected area */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <AdminShell>
                <ErrorBoundary>
                  <Suspense
                    fallback={
                      <div className="space-y-3">
                        <Skeleton className="h-8 w-56" />
                        <Skeleton className="h-32 w-full" />
                      </div>
                    }
                  >
                    <AccessRoute>
                      <Routes>
                        {/* Static (role-aware) routes from config */}
                        {routes.map((r) => {
                          const allowed =
                            !r.roles?.length ||
                            r.roles.includes(role) ||
                            r.roles.includes("admin");
                          if (!allowed) return null;
                          return (
                            <Route
                              key={r.path}
                              path={r.path}
                              element={r.element}
                            />
                          );
                        })}

                        {/* ✅ NEW: concrete page for /admin/clients */}
                        <Route
                          path="/admin/clients"
                          element={<AdminClients mode="clients" />}
                        />

                        <Route
                          path="/admin/developer"
                          element={<AdminClients mode="developer" />}
                        />

                        <Route
                          path="/admin/clients/client-configuration/:token"
                          element={<ClientConfiguration />}
                        />

                        <Route
                          path="/admin/developer/client-configuration/:token"
                          element={<ClientConfiguration />}
                        />
                        <Route
                          path="/admin/home"
                          element={<AdminDashboard />}
                        />
                        <Route
                          path="/admin/payout"
                          element={<TabbedPayoutLogs />}
                        />
                        <Route
                          path="/admin/upi-report"
                          element={<AdminUpiPayment />}
                        />
                        <Route
                          path="/admin/logs"
                          element={<AdminCampaignLog />}
                        />
                        {/* ✅ WhatsApp hub: list of clients */}
                        <Route
                          path="/admin/whatsapp"
                          element={<AdminWhatsApp />}
                        />

                        {/* ✅ Templates list for a client */}
                        <Route
                          path="/admin/templates/:clientId"
                          element={<AdminTemplate />}
                        />

                        {/* ✅ Template detail / preview (phone-style) */}
                        <Route
                          path="/admin/templatesview/:clientId/:templateName"
                          element={<AdminTemplateView />}
                        />

                        {/* ✅ Campaigns list for a client */}
                        <Route
                          path="/admin/campaigns/:clientId"
                          element={<AdminCampaign />}
                        />

                        {/* ✅ Campaign detail / preview */}
                        <Route
                          path="/admin/campaignview/:clientId/:number/:CampaignId/:campaignName"
                          element={<AdminCampaignView />}
                        />

                        <Route path="/admin/chaafo" element={<AdminChaafo />} />

                        {/* Existing Domains page */}
                        <Route
                          path="/admin/chaafo/domains"
                          element={<AdminSubdomainTabs />}
                        />

                        <Route
                          path="/admin/white-labels"
                          element={<AdminWhiteLabels />}
                        />
                        <Route
                          path="/admin/Support/Tickets"
                          element={<AdminSupportTickets />}
                        />
                        <Route path="/admin/support" element={<Support />} />
                        <Route
                          path="/admin/orders/PosOrders"
                          element={<AdminOrderController />}
                        />
                        <Route
                          path="/admin/orders/Pos"
                          element={<AdminPosOrders />}
                        />
                        <Route
                          path="/admin/orders/Dinning"
                          element={<AdminDining />}
                        />
                        <Route
                          path="/admin/orders/Dinning/qrcodes/:clientId/:clientName"
                          element={<AdminQrCode />}
                        />
                        <Route
                          path="/admin/orders/settlement"
                          element={<AdminOrderSettlement />}
                        />
                        <Route
                          path="/admin/orders/settlement/Report/:clientId/:clientName"
                          element={<AdminPaymentReports />}
                        />
                        <Route
                          path="/admin/orders/settlement/:clientId/:clientName"
                          element={<AdminSettlementAnalytics />}
                        />
                        <Route
                          path="/admin/orders/Catalogue"
                          element={<AdminCatalogue />}
                        />

                        <Route
                          path="/Orders/Catalogue/edit-ingredients/:itemId"
                          element={<EditIngredients userID={userID} />}
                        />
                        <Route
                          path="/admin/billing"
                          element={<AdminBilling />}
                        />
                        <Route
                          path="/admin/billing/bill/:clientId/:clientName"
                          element={<ClientBills />}
                        />
                        <Route
                          path="/admin/billing/insights/:clientId/:clientName"
                          element={<ClientInsights />}
                        />

                        <Route
                          path="/admin/orders/payout/:userId/:orgName"
                          element={<PayOut />}
                        />
                        <Route path="/admin/orders" element={<Orders />} />
                        <Route
                          path="/admin/all-transaction"
                          element={<AdminAllTransactionsReport />}
                        />
                        <Route
                          path="/admin/supper-admin"
                          element={<SupperAdmin />}
                        />
                        <Route
                          path="/admin/orders/settlement-report/:clientId/:clientName"
                          element={<AdminSettlement />}
                        />
                        <Route
                          path="/admin/orders/restaurant-wise/:clientId/:clientName"
                          element={<RestaurantWiseTransactions />}
                        />
                        <Route path="/admin/recon" element={<Recon />}>
                          <Route index element={<ReconSettlement />} />
                          <Route
                            path="transactions"
                            element={<ReconAllTransactionsReport />}
                          />
                          <Route
                            path="order-controller"
                            element={<ReconOrderController />}
                          />
                          <Route
                            path="all-orders-list"
                            element={<ReconAllOrders />}
                          />
                          <Route
                            path="settlement-report/:clientId/:clientName"
                            element={<SettlementReport />}
                          />
                          <Route
                            path="restaurant-wise/:clientId/:clientName"
                            element={<ReconRewise />}
                          />
                          <Route
                            path="global-inventory"
                            element={<GlobalInventory />}
                          />
                        </Route>

                        {/* Keep generic placeholder for any other /admin/* paths */}
                        <Route path="/admin/*" element={<ComingSoon />} />
                      </Routes>
                    </AccessRoute>
                  </Suspense>
                </ErrorBoundary>
              </AdminShell>
            </ProtectedRoute>
          }
        />
      </Routes>

      {/* <ToastContainer position="top-right" autoClose={3000} /> */}

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        pauseOnFocusLoss
        pauseOnHover
        draggable
        limit={1}
        style={{ top: "72px" }}
      />
    </>
  );
}
/* ----------------------------------------------------------------------------- */

export default function App() {
  return <AppWrapper />;
}
